import {renderDymekPage} from './page-view.js';

function text(value,max=4000){
  const result=String(value??'').trim();
  if(!result)throw new TypeError('prompt is required');
  return result.slice(0,max);
}
function contextLabel(context={}){
  switch(String(context.surface||'home')){
    case 'recipe': return 'Ta receptura';
    case 'planner': return 'Ten plan Smart Plannera';
    case 'active_session': return 'To aktywne wędzenie';
    case 'completed_session': return 'Ta zakończona sesja';
    case 'session': return 'To wędzenie';
    default: return 'Dymistrz';
  }
}
function prompts(context={}){
  switch(String(context.surface||'home')){
    case 'recipe':
      return Object.freeze([
        'Co w tej recepturze jest najważniejsze?',
        'Czy są tu zweryfikowane zasady bezpieczeństwa?',
        'Jak ten plan łączy się z moją wędzarnią?'
      ]);
    case 'planner':
      return Object.freeze([
        'Jakie założenia ma ten harmonogram?',
        'Co zostało zmienione ręcznie?',
        'Kiedy powinienem zacząć?'
      ]);
    case 'active_session':
      return Object.freeze([
        'Na jakim etapie jestem teraz?',
        'Jaka jest ostatnia zapisana temperatura?',
        'Co ostatnio zrobiłem?',
        'Czy są tu zweryfikowane zasady bezpieczeństwa?'
      ]);
    case 'completed_session':
      return Object.freeze([
        'Co wydarzyło się w tej sesji?',
        'Co było inne ostatnim razem?',
        'Porównaj dwie zakończone próby'
      ]);
    case 'session':
      return Object.freeze([
        'Jaki jest stan tej sesji?',
        'Jakie są założenia planu?',
        'Porównaj z poprzednią próbą'
      ]);
    default:
      return Object.freeze([
        'Co potrafisz sprawdzić w moich danych?',
        'Porównaj dwie zakończone sesje',
        'Jakie zweryfikowane zasady bezpieczeństwa są dostępne?'
      ]);
  }
}
function frozenMessage(role,{
  prompt=null,response=null,streamText=null,errorCode=null,
  interactionId=null,feedback=null
}={}){
  return Object.freeze({
    role,
    prompt:prompt==null?null:String(prompt),
    response:response||null,
    streamText:streamText==null?null:String(streamText),
    errorCode:errorCode==null?null:String(errorCode),
    interactionId:interactionId==null?null:String(interactionId),
    feedback:feedback==null?null:Boolean(feedback)
  });
}
function errorCode(error){
  return String(error?.code||error?.message||'DYMEK_RESPONSE_FAILED').slice(0,120);
}

export function createDymekPageController({
  orchestrator,
  analytics=null,
  now=()=>Date.now(),
  idFactory=()=>globalThis.crypto?.randomUUID?.()||('dymek_'+Date.now().toString(36))
}={}){
  if(!orchestrator?.respond)throw new TypeError('Dymek orchestrator is required');

  function analyticsContext(extra={}){
    return Object.freeze({
      contextSurface:context.surface,
      sessionId:context.sessionId,
      sourceSessionId:context.sourceSessionId,
      plannerRunId:context.plannerRunId,
      ...extra
    });
  }
  function track(method,input={}){
    try{return analytics?.[method]?.(analyticsContext(input));}
    catch{return null;}
  }

  let context=Object.freeze({surface:'home'});
  let messages=[];
  let retryRequest=null;
  let pendingHistoryPrompt=null;
  let state=Object.freeze({
    kind:'ready',
    context,
    contextLabel:contextLabel(context),
    suggestedPrompts:prompts(context),
    messages:Object.freeze([]),
    historyChoices:Object.freeze([]),
    notice:'',
    noticeKind:'neutral'
  });

  function setState(next={}){
    state=Object.freeze({
      ...state,
      ...next,
      context,
      contextLabel:contextLabel(context),
      suggestedPrompts:prompts(context),
      messages:Object.freeze([...messages])
    });
    return state;
  }

  function openContext(next={}){
    context=Object.freeze({
      surface:String(next.surface||'home'),
      sessionId:next.sessionId?String(next.sessionId):null,
      recipeId:next.recipeId?String(next.recipeId):null,
      sourceSessionId:next.sourceSessionId?String(next.sourceSessionId):null,
      plannerRunId:next.plannerRunId?String(next.plannerRunId):null
    });
    messages=[];
    retryRequest=null;
    pendingHistoryPrompt=null;
    state=Object.freeze({
      kind:'ready',
      context,
      contextLabel:contextLabel(context),
      suggestedPrompts:prompts(context),
      messages:Object.freeze([]),
      historyChoices:Object.freeze([]),
      notice:'',
      noticeKind:'neutral'
    });
    track('opened');
    return state;
  }

  function clearConversation(){
    messages=[];
    retryRequest=null;
    pendingHistoryPrompt=null;
    return setState({
      kind:'ready',
      historyChoices:Object.freeze([]),
      notice:'Bieżąca rozmowa została usunięta z pamięci aplikacji.',
      noticeKind:'success'
    });
  }

  function newTopic(){
    messages=[];
    retryRequest=null;
    pendingHistoryPrompt=null;
    return setState({
      kind:'ready',
      historyChoices:Object.freeze([]),
      notice:'Nowy temat — kontekst pozostaje ten sam.',
      noticeKind:'neutral'
    });
  }

  async function resolveResponse(result,promptValue){
    if(result&&typeof result[Symbol.asyncIterator]==='function'){
      let final=null;
      let stream='';
      for await(const event of result){
        if(event?.type==='delta'){
          stream+=String(event.text||'');
          const streaming=frozenMessage('assistant',{streamText:stream});
          setState({kind:'streaming'});
          state=Object.freeze({...state,messages:Object.freeze([...messages,streaming])});
        }else if(event?.type==='final'){
          final=event.response||event.value||null;
        }
      }
      if(!final)throw new Error('DYMEK_STREAM_NO_FINAL_RESPONSE');
      return final;
    }
    return result;
  }

  async function submit(prompt,{
    selectedHistoryIds=[],
    privateTextNeeded=false,
    providerProcessingConsent=false,
    retry=false,
    interactionId=null
  }={}){
    const value=text(prompt);
    const resolvedInteractionId=String(interactionId||idFactory()).slice(0,80);
    const started=Number(now());
    const request=Object.freeze({
      prompt:value,
      surfaceContext:context,
      selectedHistoryIds:Object.freeze([...(selectedHistoryIds||[])].map(String)),
      privateTextNeeded:Boolean(privateTextNeeded),
      privateTextConsent:Boolean(privateTextNeeded),
      providerProcessingConsent:Boolean(providerProcessingConsent),
      retry:Boolean(retry),
      interactionId:resolvedInteractionId
    });
    retryRequest=request;
    track('promptSubmitted',{
      interactionId:resolvedInteractionId,
      privateTextIncluded:Boolean(privateTextNeeded),
      providerConsent:Boolean(providerProcessingConsent),
      retry:Boolean(retry)
    });
    messages.push(frozenMessage('user',{prompt:value,interactionId:resolvedInteractionId}));
    setState({
      kind:'streaming',
      historyChoices:Object.freeze([]),
      notice:'',
      noticeKind:'neutral'
    });

    try{
      const raw=await orchestrator.respond(request);
      const response=await resolveResponse(raw,value);
      if(response?.status==='needs_history_selection'){
        pendingHistoryPrompt=value;
        track('responseCompleted',{
          interactionId:resolvedInteractionId,
          response,
          latencyMs:Math.max(0,Number(now())-started)
        });
        return setState({
          kind:'history_selection',
          historyChoices:Object.freeze([...(response.choices||[])]),
          notice:'Wybierz dwie zakończone sesje do porównania.',
          noticeKind:'neutral'
        });
      }
      messages.push(frozenMessage('assistant',{
        response,
        interactionId:resolvedInteractionId
      }));
      track('responseCompleted',{
        interactionId:resolvedInteractionId,
        response,
        latencyMs:Math.max(0,Number(now())-started)
      });
      retryRequest=null;
      pendingHistoryPrompt=null;
      return setState({
        kind:'ready',
        historyChoices:Object.freeze([]),
        notice:'',
        noticeKind:'neutral'
      });
    }catch(error){
      const code=errorCode(error);
      messages.push(frozenMessage('assistant',{
        errorCode:code,
        interactionId:resolvedInteractionId
      }));
      track('responseFailed',{
        interactionId:resolvedInteractionId,
        errorCode:code,
        latencyMs:Math.max(0,Number(now())-started)
      });
      return setState({
        kind:'error',
        historyChoices:Object.freeze([]),
        notice:'Nie udało się przygotować odpowiedzi. Możesz ponowić bez zmiany danych sesji.',
        noticeKind:'error'
      });
    }
  }

  async function compareHistory(selectedIds,options={}){
    const ids=[...(selectedIds||[])].map(String).filter(Boolean);
    if(ids.length!==2)throw new TypeError('Select exactly two history sessions');
    const prompt=pendingHistoryPrompt||retryRequest?.prompt||'Porównaj wybrane sesje';
    return submit(prompt,{...options,selectedHistoryIds:ids});
  }

  async function retry(){
    if(!retryRequest)throw new Error('DYMEK_NOTHING_TO_RETRY');
    const request=retryRequest;
    if(messages.at(-1)?.errorCode)messages.pop();
    if(messages.at(-1)?.role==='user')messages.pop();
    return submit(request.prompt,{
      selectedHistoryIds:request.selectedHistoryIds,
      privateTextNeeded:request.privateTextNeeded,
      providerProcessingConsent:request.providerProcessingConsent,
      retry:true,
      interactionId:request.interactionId
    });
  }

  function submitFeedback(interactionId,helpful){
    const id=String(interactionId||'').trim();
    if(!id)throw new TypeError('interactionId is required');
    let found=false;
    messages=messages.map(item=>{
      if(item.role!=='assistant'||item.interactionId!==id||!item.response)return item;
      found=true;
      return frozenMessage('assistant',{
        response:item.response,
        interactionId:id,
        feedback:Boolean(helpful)
      });
    });
    if(!found)throw new Error('DYMEK_RESPONSE_NOT_FOUND');
    track('feedbackSubmitted',{interactionId:id,helpful:Boolean(helpful)});
    return setState({});
  }

  function referenceOpened(referenceKind){
    const latest=[...messages].reverse().find(item=>item.role==='assistant'&&item.interactionId);
    track('referenceOpened',{
      interactionId:latest?.interactionId||String(idFactory()).slice(0,80),
      referenceKind
    });
  }

  function getState(){return state;}
  function render(){return renderDymekPage(state);}

  return Object.freeze({
    openContext,newTopic,clearConversation,submit,compareHistory,retry,
    submitFeedback,referenceOpened,getState,render
  });
}
